/**
 * EventDetailOverlay — 일정 상세 모달 (visibility 모델)
 *
 * 일정 메타 + 공개 범위 + 참석자 응답 + 수정/삭제.
 * Step 7 에서 댓글 추가 예정.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Popover, PopoverContent, PopoverAnchor } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { toast } from '../../components/ui/use-toast';
import { ToastAction } from '../../components/ui/toast';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useCalendarContext } from '../../context/CalendarContext';
import { useAppUIContext } from '../../context/AppUIContext';
import { My } from '../../services/My';
import {
  CalendarEvent, EventAttendee, AttendeeStatus,
  canManageEvent, categorizeEvent, eventChipStyle,
} from '../../models/CalendarEvent';
import {
  deleteEvent, fetchAttendees, respondToInvite, fetchMyReminders, setMyReminders,
} from '../../services/CalendarService';
import { Fire } from '../../services/FireClient';
import { CalendarDays, MapPin, Globe, Hash, Lock, Trash2, Pencil, Users, Bell, Building2 } from 'lucide-react';
import { AdminBadge } from '../components/AdminBadge';
import EventCreateModal from './EventCreateModal';
import { UserAvatar } from '../components/UserAvatar';
import { useCalendarLabels } from './useCalendarLabels';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: CalendarEvent | null;
  /** 팝오버 앵커 — 클릭한 일정 칩 element (virtualRef). 미제공 시(예: 통지모달) 화면 중앙에 표시. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** 팝오버 가로 정렬 (기본 'start' — 칩 옆. 통지모달은 'center' — 모달 중앙) */
  align?: 'start' | 'center' | 'end';
}

function formatDateTime(iso: string, isAllDay: boolean): string {
  const d = new Date(iso);
  if (isAllDay) {
    return d.toLocaleDateString(i18n.locale || 'en', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
  }
  return d.toLocaleString(i18n.locale || 'en', {
    year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

const EventDetailOverlay: React.FC<Props> = ({ open, onOpenChange, event: propEvent, anchorRef, align = 'start' }) => {
  const myRole = useMyRole();
  const { events, setEvents, refreshEvents } = useCalendarContext();
  const { rooms, activeWorkspaceId } = useAppUIContext();
  // 편집 저장 후 events(context)가 갱신되면 최신 반영 — prop(propEvent)은 팝오버 열 때의 옛 객체일 수 있음.
  // events 에 같은 id 가 있으면 그걸(최신) 쓰고, 없으면(통지모달 fetchEvent 등) prop 그대로.
  const event = useMemo(() => events.find((e) => e.id === propEvent?.id) ?? propEvent, [events, propEvent]);
  // anchorRef 미제공(예: 통지모달) 시 화면 중앙을 가상 앵커로 사용
  const centerAnchorRef = useRef<{ getBoundingClientRect(): DOMRect }>({
    getBoundingClientRect: () => new DOMRect(window.innerWidth / 2, window.innerHeight / 2, 0, 0),
  });
  const effectiveAnchorRef = anchorRef ?? centerAnchorRef;
  const [showEdit, setShowEdit] = useState(false);
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  // 참석자 이름 표시용 — event_attendees 에는 user_id 만 저장되므로 워크스페이스 멤버에서 해석
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  const [respondingStatus, setRespondingStatus] = useState<AttendeeStatus | null>(null);
  // 내 per-user 리마인더 (분 단위, 단일 값 UI). [] = 없음
  const [myReminders, setMyRemindersState] = useState<number[]>([]);
  const [savingReminder, setSavingReminder] = useState(false);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    Fire.ws.getWorkspaceMembers(activeWorkspaceId).then((members) => {
      const map: Record<string, string> = {};
      (members ?? []).forEach((m) => {
        if (m?.id) map[m.id] = m.user?.name ?? m.user?.email ?? '';
      });
      setMemberNames(map);
    }).catch(() => {});
  }, [open, activeWorkspaceId]);

  useEffect(() => {
    if (!open || !event) return;
    fetchAttendees(event.id).then(setAttendees).catch(() => setAttendees([]));
    // 가상 일정 (공휴일/task) 은 리마인더 대상 아님 — 실제 event 만
    const isVirtual = !!event.metadata?.__holiday || !!event.metadata?.__task;
    if (!isVirtual && My.user.id) {
      // 반복 인스턴스 id "origId::iso" → 원본 id 기준
      const baseId = event.id.split('::')[0];
      fetchMyReminders(baseId, My.user.id)
        .then(setMyRemindersState)
        .catch(() => setMyRemindersState([]));
    } else {
      setMyRemindersState([]);
    }
  }, [open, event?.id]);

  const handleReminderChange = async (v: string) => {
    if (!event) return;
    const next = v === 'none' ? [] : [parseInt(v, 10)];
    setMyRemindersState(next);
    setSavingReminder(true);
    try {
      const baseId = event.id.split('::')[0];
      await setMyReminders(baseId, My.user.id, next);
      toast({ description: next.length === 0 ? t`Reminder cleared` : t`Reminder set` });
    } catch (err: any) {
      toast({ description: err?.message ?? t`Failed to set reminder`, variant: 'destructive' });
    } finally {
      setSavingReminder(false);
    }
  };

  const channelNames = useMemo(() => {
    if (!event || event.visibility !== 'public_channels') return [];
    return event.shared_channel_ids
      .map((cid) => rooms.find((r) => r.id === cid)?.name ?? '(unknown)');
  }, [event?.shared_channel_ids, rooms]);

  const ownerChannelName = useMemo(() => {
    if (!event?.owner_channel_id) return null;
    return rooms.find((r) => r.id === event.owner_channel_id)?.name ?? '(unknown)';
  }, [event?.owner_channel_id, rooms]);

  // 본인이 admin/owner 인 채널 id 집합
  const myAdminChannelIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rooms) {
      if ((r as any).isOwner || (r as any).isAdmin) set.add(r.id);
    }
    return set;
  }, [rooms]);

  const canEdit = useMemo(() => {
    if (!event) return false;
    return canManageEvent(event, myRole ?? 'guest', My.user.id, (cid) => myAdminChannelIds.has(cid));
  }, [event, myRole, myAdminChannelIds]);

  // 카테고리 라벨 (상세 모달 상단)
  const myChannelIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rooms) {
      if ((r.visibility === 'listed' || r.visibility === 'unlisted') && !r.isOneToOne) set.add(r.id);
    }
    return set;
  }, [rooms]);

  const labels = useCalendarLabels();
  const categoryStyle = useMemo(() => {
    if (!event) return null;
    const isAttendee = attendees.some((a) => a.user_id === My.user.id);
    const cat = categorizeEvent(event, My.user.id, myChannelIds, isAttendee);
    return { ...eventChipStyle(cat), categoryLabel: labels.category(cat) };
  }, [event, attendees, myChannelIds, labels]);

  const myAttendance = useMemo(
    () => attendees.find((a) => a.user_id === My.user.id) ?? null,
    [attendees],
  );

  // 로딩 중(event 아직 미조회)에도 앵커 위치에 팝오버를 열고 스피너 표시 (통지모달에서 fetchEvent 대기)
  if (!event) {
    if (!open) return null;
    return (
      <Popover open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }} modal>
        <PopoverAnchor virtualRef={effectiveAnchorRef as unknown as React.RefObject<{ getBoundingClientRect(): DOMRect }>} />
        <PopoverContent align={align} side="bottom" sideOffset={6} collisionPadding={12} className="w-[min(28rem,92vw)]">
          {/* 로딩 스켈레톤 — 일정 상세(제목·시간·위치) 레이아웃을 미리 잡아 깜빡임 최소화 */}
          <div className="flex flex-col gap-3 animate-pulse" aria-busy="true">
            <div className="flex items-center gap-2">
              <div className="size-2.5 rounded-full bg-muted shrink-0" />
              <div className="h-5 w-2/3 rounded bg-muted" />
            </div>
            <div className="flex items-start gap-2.5">
              <div className="size-4 rounded bg-muted shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                <div className="h-4 w-1/2 rounded bg-muted" />
                <div className="h-3 w-1/3 rounded bg-muted" />
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <div className="size-4 rounded bg-muted shrink-0 mt-0.5" />
              <div className="h-4 w-2/5 rounded bg-muted" />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // 구글 캘린더식: 확인창 없이 즉시 화면에서 제거 + "실행취소" 토스트.
  // 토스트가 유지되는 동안 유예하고, 사라지면 실제 hard delete. 실행취소 시 재조회로 복원.
  const handleDelete = () => {
    const ev = event;
    const UNDO_MS = 5000;
    setEvents(events.filter((e) => e.id !== ev.id)); // 낙관적 제거
    onOpenChange(false);

    let undone = false;
    const timer = setTimeout(() => {
      if (undone) return;
      deleteEvent(ev.id)
        .then(() => { refreshEvents(); }) // 캐시 동기화
        .catch((err: any) => {
          console.error('[EventDetailOverlay] 삭제 실패:', err);
          toast({ description: err?.message ?? t`Failed to delete`, variant: 'destructive' });
          refreshEvents(); // 실패 시 화면 복원
        });
    }, UNDO_MS);

    toast({
      description: t`Event deleted`,
      duration: UNDO_MS,
      action: (
        <ToastAction altText={t`Undo`} onClick={() => {
          undone = true;
          clearTimeout(timer);
          refreshEvents(); // DB 에 아직 있으므로 재조회로 복원
        }}>
          <Trans>Undo</Trans>
        </ToastAction>
      ),
    });
  };

  const handleRespond = async (status: AttendeeStatus) => {
    try {
      setRespondingStatus(status);
      await respondToInvite(event.id, My.user.id, status);
      // 다시 fetch
      const list = await fetchAttendees(event.id);
      setAttendees(list);
      toast({ description: t`Responded as ${labels.attendeeStatus(status)}` });
    } catch (err: any) {
      console.error('[EventDetailOverlay] 응답 실패:', err);
      toast({ description: err?.message ?? t`Failed to respond`, variant: 'destructive' });
    } finally {
      setRespondingStatus(null);
    }
  };

  const VisIcon = event.visibility === 'private' ? Lock
    : event.visibility === 'public_channels' ? Hash
    : Building2;

  return (
    <>
      {/* 상세 = 팝오버 (클릭한 칩에 앵커). 수정 클릭 시 팝오버 닫고 편집 모달로 전환.
          modal: 바깥 클릭이 아래 요소(다른 칩/셀)로 전파되지 않고 팝오버만 닫히도록 (프로필 DropdownMenu 와 동일) */}
      <Popover open={open && !showEdit} onOpenChange={(o) => { if (!o) onOpenChange(false); }} modal>
        <PopoverAnchor virtualRef={effectiveAnchorRef as unknown as React.RefObject<{ getBoundingClientRect(): DOMRect }>} />
        <PopoverContent
          align={align}
          side="bottom"
          sideOffset={6}
          collisionPadding={12}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="w-[min(28rem,92vw)] max-h-[80vh] overflow-y-auto"
        >
          <div className="flex items-center gap-2 mb-3">
            <span
              className="size-2.5 rounded-full shrink-0 bg-primary"
              style={event.color ? { backgroundColor: event.color } : undefined}
            />
            <span className="min-w-0 truncate text-h5 font-semibold text-foreground">{event.title}</span>
            {/* 전사 일정 — 관리자 전용 표시 (게시판 화면과 동일 AdminBadge) */}
            {event.visibility === 'public_workspace' && <AdminBadge className="shrink-0" />}
          </div>

          <div className="flex flex-col gap-3 text-body">
            {/* 시간 */}
            <div className="flex items-start gap-2.5">
              <CalendarDays className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex flex-col">
                <span className="text-foreground">{formatDateTime(event.start_at, event.is_all_day)}</span>
                <span className="text-muted-foreground text-caption">→ {formatDateTime(event.end_at, event.is_all_day)}</span>
                <span className="text-meta text-muted-foreground mt-0.5">
                  {event.is_all_day ? <Trans>All day</Trans> : event.timezone}
                </span>
              </div>
            </div>

            {/* 위치 */}
            {event.location && (
              <div className="flex items-center gap-2.5">
                <MapPin className="size-4 text-muted-foreground shrink-0" />
                <span className="text-foreground">{event.location}</span>
              </div>
            )}

            {/* 소유권 / 공개 범위 — owner_channel_id 가 있으면 채널 소유, 없으면 개인 */}
            <div className="flex items-start gap-2.5">
              <VisIcon className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <div className="flex flex-col">
                {event.owner_channel_id ? (
                  <span className="text-foreground flex items-center gap-1.5">
                    <Hash className="size-3.5 text-primary" />
                    <Trans>Channel event · #{ownerChannelName}</Trans>
                  </span>
                ) : event.visibility === 'public_workspace' ? (
                  <span className="text-foreground"><Trans>Workspace event</Trans></span>
                ) : (
                  <span className="text-foreground">{labels.visibility(event.visibility)}</span>
                )}
                {channelNames.length > 0 && (
                  <span className="text-meta text-muted-foreground">
                    {event.owner_channel_id ? t`Additional shared: ` : t`Shared channels: `}
                    {channelNames.map((n) => `#${n}`).join(', ')}
                  </span>
                )}
                {categoryStyle && (
                  <span className="text-meta text-muted-foreground mt-0.5">{categoryStyle.categoryLabel}</span>
                )}
              </div>
            </div>

            {/* 리마인더 */}
            {event.reminders && event.reminders.length > 0 && (
              <div className="flex items-center gap-2.5">
                <Bell className="size-4 text-muted-foreground shrink-0" />
                <span className="text-foreground">
                  {event.reminders.map((m) =>
                    m === 0 ? t`At start time`
                    : m < 60 ? t`${m} minutes before`
                    : m < 1440 ? t`${Math.floor(m / 60)} hours before`
                    : t`${Math.floor(m / 1440)} days before`,
                  ).join(', ')}
                </span>
              </div>
            )}

            {/* 설명 */}
            {event.description && (
              <div className="mt-2 pt-3 border-t border-border/40">
                <p className="text-body whitespace-pre-wrap text-foreground">{event.description}</p>
              </div>
            )}

            {/* 참석자 — 전사 공개 일정은 RSVP 개념 미적용 → 표시 X */}
            {event.visibility !== 'public_workspace' && attendees.length > 0 && (
              <div className="mt-2 pt-3 border-t border-border/40 flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-label font-medium text-foreground">
                  <Users className="size-4 text-muted-foreground" />
                  <Trans>Attendees (<span>{attendees.length}</span>)</Trans>
                </div>
                <ul className="flex flex-col gap-1">
                  {attendees.map((a) => (
                    <li key={a.user_id} className="flex items-center gap-2 text-caption">
                      <span className="text-foreground">{a.user_id === My.user.id ? t`Me` : (memberNames[a.user_id] || t`(No name)`)}</span>
                      <span className={
                        'text-meta px-1.5 py-0.5 rounded ' +
                        (a.status === 'accepted' ? 'bg-badge-success-bg text-badge-success'
                          : a.status === 'declined' ? 'bg-destructive/10 text-destructive'
                          : a.status === 'tentative' ? 'bg-badge-caution-bg text-badge-caution'
                          : 'bg-muted text-muted-foreground')
                      }>
                        {labels.attendeeStatus(a.status)}
                      </span>
                      {a.is_organizer && <span className="text-meta text-primary"><Trans>Organizer</Trans></span>}
                    </li>
                  ))}
                </ul>

                {/* 본인 응답 — 초대받았고 organizer 아닐 때 */}
                {myAttendance && !myAttendance.is_organizer && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-label text-muted-foreground"><Trans>My response:</Trans></span>
                    {(['accepted', 'tentative', 'declined'] as AttendeeStatus[]).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={myAttendance.status === s ? 'default' : 'outline'}
                        onClick={() => handleRespond(s)}
                        disabled={respondingStatus !== null}
                      >
                        {labels.attendeeStatus(s)}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 내 리마인더 — per-user. 가상 일정(공휴일/task) 제외, 누구나 자기 알림 설정 */}
            {!event.metadata?.__holiday && !event.metadata?.__task && (
              <div className="mt-2 pt-3 border-t border-border/40 flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5 text-label font-medium text-foreground">
                  <Bell className="size-4 text-muted-foreground" />
                  <Trans>My reminder</Trans>
                </div>
                <Select
                  value={myReminders.length === 0 ? 'none' : String(myReminders[0])}
                  onValueChange={handleReminderChange}
                  disabled={savingReminder}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><Trans>None</Trans></SelectItem>
                    <SelectItem value="0"><Trans>At start time</Trans></SelectItem>
                    <SelectItem value="5"><Trans>5 minutes before</Trans></SelectItem>
                    <SelectItem value="10"><Trans>10 minutes before</Trans></SelectItem>
                    <SelectItem value="15"><Trans>15 minutes before</Trans></SelectItem>
                    <SelectItem value="30"><Trans>30 minutes before</Trans></SelectItem>
                    <SelectItem value="60"><Trans>1 hour before</Trans></SelectItem>
                    <SelectItem value="120"><Trans>2 hours before</Trans></SelectItem>
                    <SelectItem value="1440"><Trans>1 day before</Trans></SelectItem>
                    <SelectItem value="2880"><Trans>2 days before</Trans></SelectItem>
                    <SelectItem value="10080"><Trans>1 week before</Trans></SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-meta text-muted-foreground">
                  <Trans>This reminder applies only to you. It doesn't affect other members.</Trans>
                </p>
              </div>
            )}

            {/* 작성자 */}
            <div className="mt-2 pt-2 border-t border-border/40 text-meta text-muted-foreground">
              <Trans>Created by: {event.creator?.name ?? '(unknown)'}</Trans> · {new Date(event.created_at).toLocaleDateString(i18n.locale || 'en')}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-border/40">
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="size-3.5 mr-1.5" />
                  <Trans>Delete</Trans>
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
                  <Pencil className="size-3.5 mr-1.5" />
                  <Trans>Edit</Trans>
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => onOpenChange(false)}><Trans>Close</Trans></Button>
          </div>
        </PopoverContent>
      </Popover>

      {showEdit && (
        <EventCreateModal
          open={showEdit}
          onOpenChange={(v) => setShowEdit(v)} // 편집 모달 닫으면 상세 팝오버로 복귀 (전체 닫지 않음)
          event={event}
        />
      )}
    </>
  );
};

export default EventDetailOverlay;
